// src/newDashboard/shared/PlanCancel.jsx
//
// "Cancel plan" / "Keep my plan" for a bar's monthly plan, shown on the
// Subscription page. Canceling stops future charges; the bar keeps the
// plan until the end of the month it already paid for.

import React, { useEffect, useState } from "react";
import { auth } from "../../firebase";

const URL = "https://us-central1-bar-buzz-a6645.cloudfunctions.net/managePlan";

async function call(body) {
  const token = await auth.currentUser?.getIdToken();
  const res = await fetch(URL, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || "Something went wrong. Try again.");
  return data;
}

function niceDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

const linkBtn = {
  background: "transparent",
  border: "1px solid #5A5070",
  color: "#fff",
  borderRadius: "10px",
  minHeight: "44px",
  padding: "0 16px",
  font: "600 15px Inter, sans-serif",
  cursor: "pointer",
  marginTop: "10px",
};

export default function PlanCancel({ barId, plan, planName }) {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    setInfo(null);
    setError("");
    call({ barId, plan, action: "status" })
      .then((d) => alive && setInfo(d))
      .catch((e) => alive && setError(e.message));
    return () => {
      alive = false;
    };
  }, [barId, plan]);

  const act = async (action) => {
    if (action === "cancel") {
      const ok = window.confirm(
        `Cancel ${planName}?\n\nYou won't be charged again. You keep ${planName} until ${
          niceDate(info?.periodEnd) || "the end of the month you paid for"
        }. You can change your mind before then.`
      );
      if (!ok) return;
    }
    setBusy(true);
    setError("");
    try {
      setInfo(await call({ barId, plan, action }));
    } catch (e) {
      setError(e.message);
    }
    setBusy(false);
  };

  if (error) return <p style={{ color: "#f87171", margin: "8px 0 0", fontSize: "15px" }}>{error}</p>;
  if (!info) return <p style={{ color: "#ACA8BF", margin: "8px 0 0", fontSize: "15px" }}>Checking your plan…</p>;

  if (info.noStripe) {
    return (
      <p style={{ color: "#ACA8BF", margin: "8px 0 0", fontSize: "15px" }}>
        To change this plan, email <a href="mailto:hello@barfunfinder.com" style={{ color: "#FF6FA3" }}>hello@barfunfinder.com</a>.
      </p>
    );
  }

  if (info.cancelAtPeriodEnd) {
    return (
      <div>
        <p style={{ color: "#FBBF24", margin: "8px 0 0", fontSize: "15px", fontWeight: 600 }}>
          Canceled. {planName} ends {niceDate(info.periodEnd) || "at the end of this month"}. You won't be charged again.
        </p>
        <button type="button" style={linkBtn} disabled={busy} onClick={() => act("resume")}>
          {busy ? "One moment…" : `Keep ${planName}`}
        </button>
      </div>
    );
  }

  return (
    <div>
      {info.periodEnd && (
        <p style={{ color: "#ACA8BF", margin: "8px 0 0", fontSize: "15px" }}>Renews {niceDate(info.periodEnd)}.</p>
      )}
      <button type="button" style={linkBtn} disabled={busy} onClick={() => act("cancel")}>
        {busy ? "One moment…" : "Cancel plan"}
      </button>
    </div>
  );
}
